import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import i18n from 'i18next'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  useGetUserSession,
  useLogin,
  useSignUp,
  type SignUpPayload,
} from '@/shared/hooks/api/auth'
import { useUpdateLanguage } from '@/shared/hooks/api/user'
import { SUPPORTED_LANGUAGES } from '@/shared/config/languages'
import { AuthLayout } from '@/features/auth/auth-layout'
import { SignUpForm } from '@/features/auth/sign-up/sign-up-form'

function isSupportedInterfaceLanguage(code: string): boolean {
  return SUPPORTED_LANGUAGES.some((lang) => lang.value === code)
}

export function SignUp() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') ?? '/'
  const { refetch: getSession } = useGetUserSession()
  const { mutateAsync: signUp, isPending: isSigningUp } = useSignUp()
  const { mutateAsync: login } = useLogin()
  const { mutateAsync: updateLanguage } = useUpdateLanguage()

  async function handleSignUp(payload: SignUpPayload) {
    const signUpResult = await signUp(payload)
    if (signUpResult.success === false) {
      if (signUpResult.error === 'USER_ALREADY_EXISTS') {
        toast.error(t('signUp.errors.emailExists'))
      } else {
        toast.error(t('signUp.errors.signUpFailed'))
      }
      return
    }

    try {
      await login({ email: payload.email, password: payload.password })
      const chosenLanguage = i18n.language
      if (isSupportedInterfaceLanguage(chosenLanguage)) {
        try {
          await updateLanguage(chosenLanguage)
        } catch {
          // Language can be changed later; session is valid.
        }
      }

      await getSession()
      navigate(redirectTo, { replace: true })
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(t('signUp.errors.signUpFailed'))
        return
      }
      toast.error(t('signUp.errors.signUpFailed'))
    }
  }

  return (
    <AuthLayout>
      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="text-lg tracking-tight">
            {t('signUp.title')}
          </CardTitle>
          <CardDescription>
            {t('signUp.alreadyHaveAccount')}{' '}
            <Link
              to="/sign-in"
              className="underline underline-offset-4 hover:text-primary"
            >
              {t('signUp.signInLink')}
            </Link>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SignUpForm onSignUp={handleSignUp} isSigningUp={isSigningUp} />
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
